import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { UserRoles } from "../../app/auth/roles";
import { ApplicationStatus } from "../../app/enums/applicationStatus";
import { localStorageHelpers } from "../../app/helpers/localStorageHelpers";
import { HasRole } from "../../app/helpers/userHelpers";
import { RootState } from "../../app/store/configureStore";
import InternshipEditModal from "./InternshipEditModal";
import InternshipApplyAction from "./InternshipApplyAction";
import InternshipApplicationsModal from "./InternshipApplicationsModal";
import ResponsiblePersonModal from "../shadowings/components/ResponsiblePersonModal";
import FeedbackListModal from "../companies/components/FeedbackListModal";
import { Internship } from "./types";

interface Props {
  internship: Internship;
  handleApplicationCancel: (applicationId: number) => void;
  onUpdate?: () => void;
}

const InternshipItemActions: React.FC<Props> = ({ internship, handleApplicationCancel, onUpdate }) => {
  const { t } = useTranslation();
  const canApplyToShadowing = useSelector((state: RootState) => !!state.currentUser.seasonInfo?.canApply);

  const isStudent = HasRole(UserRoles.student);
  const isCompany = HasRole(UserRoles.company);
  const isAdmin = HasRole(UserRoles.admin);

  const isPast = () => {
    const date = internship.endDate ? new Date(internship.endDate) : new Date(internship.startDate);
    return date <= new Date();
  };

  const isOwner = isCompany && localStorageHelpers.getSubjectId() === internship.company?.id.toString();
  const notFull = internship.occupiedApplications < internship.participantsCapacity;
  const alreadyApplied = !!(isStudent && internship.applications?.length && !isPast());
  const canApply = isStudent && !alreadyApplied && !isPast() && notFull && canApplyToShadowing;
  const canSeeApplicants = (!isPast() && isOwner) || isAdmin;
  const canEditPosition = isOwner && !isPast();
  const canSeeResponsiblePerson = isOwner || isAdmin;
  const canViewNewApplications = !isPast() && (isOwner || isAdmin);
  const canSeeFeedback =
    isPast() && isOwner && !!internship.applications?.some((a) => a.status === ApplicationStatus.COMPANY_APPROVED);

  const renderAlreadyApplied = () => {
    if (internship.applications?.length !== 1) return null;
    const applicationId = internship.applications[0].id;
    return (
      <div>
        {t("shadowing.already_applied")}.{" "}
        <Link to="#" onClick={() => handleApplicationCancel(applicationId)}>
          {t("common.actions.cancel")}
        </Link>
      </div>
    );
  };

  return (
    <>
      {alreadyApplied && renderAlreadyApplied()}
      {canApply && <InternshipApplyAction internship={internship} onSuccess={onUpdate} />}
      {canSeeApplicants && (
        <div>
          <InternshipApplicationsModal approved={true} internship={internship} onUpdate={onUpdate} />
        </div>
      )}
      <div>
        {canSeeResponsiblePerson && <ResponsiblePersonModal item={internship} />}
        {canSeeResponsiblePerson && canEditPosition && " | "}
        {canEditPosition && (
          <span>
            <InternshipEditModal internship={internship} onUpdate={onUpdate}>
              <Link to="#">{t("internship.update_button")}</Link>
            </InternshipEditModal>
          </span>
        )}
      </div>
      {canSeeFeedback && (
        <div>
          <FeedbackListModal shadowing={{ applications: internship.applications ?? [] }} />
        </div>
      )}
      {canViewNewApplications && (
        <InternshipApplicationsModal approved={false} internship={internship} onUpdate={onUpdate} />
      )}
    </>
  );
};

export default InternshipItemActions;
